export interface SerpMisalignedLink {
  head: string;
  actual: string;
  expected: string;
}

export interface SerpSnippetSegment {
  text: string;
  emphasized: boolean;
  /** Amber: SERP vocabulary used in the wrong phrase context (details on hover). */
  misaligned?: boolean;
  misalignedLink?: SerpMisalignedLink;
  /** Structure green vs query-question blue (Our-page answer structure). */
  highlightKind?: 'structure' | 'question' | 'closest';
}

/**
 * Split a SERP description into plain / emphasized segments using DataForSEO `highlighted` terms.
 */
export function buildSerpSnippetSegments(
  description: string,
  highlighted: string[] | null | undefined
): SerpSnippetSegment[] {
  const text = (description || '').trim();
  if (!text) return [];

  const terms = (highlighted || [])
    .map((t) => String(t || '').trim())
    .filter((t) => t.length > 0)
    .sort((a, b) => b.length - a.length);

  if (!terms.length) {
    return [{ text, emphasized: false }];
  }

  type Range = { start: number; end: number };
  const ranges: Range[] = [];
  const lower = text.toLowerCase();

  for (const term of terms) {
    const needle = term.toLowerCase();
    let from = 0;
    while (from < lower.length) {
      const idx = lower.indexOf(needle, from);
      if (idx < 0) break;
      ranges.push({ start: idx, end: idx + term.length });
      from = idx + Math.max(1, needle.length);
    }
  }

  if (!ranges.length) {
    return [{ text, emphasized: false }];
  }

  ranges.sort((a, b) => a.start - b.start || b.end - a.end);
  const merged: Range[] = [];
  for (const range of ranges) {
    const last = merged[merged.length - 1];
    if (!last || range.start > last.end) {
      merged.push({ ...range });
    } else if (range.end > last.end) {
      last.end = range.end;
    }
  }

  const segments: SerpSnippetSegment[] = [];
  let cursor = 0;
  for (const range of merged) {
    if (range.start > cursor) {
      segments.push({ text: text.slice(cursor, range.start), emphasized: false });
    }
    segments.push({ text: text.slice(range.start, range.end), emphasized: true });
    cursor = range.end;
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), emphasized: false });
  }

  return segments.filter((seg) => seg.text.length > 0);
}

/**
 * Highlight every word in `text` whose folded form matches `term` (plural-aware).
 * Used when hovering a vocabulary chip to show winner usage.
 */
export function buildSerpFoldedTermSegments(
  text: string,
  term: string | null | undefined,
  foldToken: (token: string) => string
): SerpSnippetSegment[] {
  const raw = (text || '').trim();
  if (!raw) return [];
  const termFold = term ? foldToken(term) : '';
  if (!termFold || termFold.length < 3) {
    return [{ text: raw, emphasized: false }];
  }

  const segments: SerpSnippetSegment[] = [];
  const wordRe = /\b([a-zA-Z]{3,})\b/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = wordRe.exec(raw)) !== null) {
    const start = match.index;
    const word = match[1];
    if (start > lastIndex) {
      segments.push({ text: raw.slice(lastIndex, start), emphasized: false });
    }
    segments.push({
      text: word,
      emphasized: foldToken(word) === termFold
    });
    lastIndex = start + word.length;
  }

  if (lastIndex < raw.length) {
    segments.push({ text: raw.slice(lastIndex), emphasized: false });
  }

  return segments.filter((seg) => seg.text.length > 0);
}
